import {getWootricConfig, isEmailExcluded} from './wootric.config';
import {hasAnalyticsCookieConsent, subscribeToCookieConsentChanges} from './cookie-consent';
import type {WootricConfig, WootricRunOptions} from './wootric.types';

const DISCLAIMER = {
    text: 'Learn how we handle your feedback',
    link: 'https://www.theaccessgroup.com/en-gb/privacy-notice-for-access-products/',
    link_word: 'here'
} as const;

const MAX_RUN_ATTEMPTS = 5;
const RUN_RETRY_MS = 1000;

export function getDeviceOS(): string {
    const userAgent = navigator.userAgent || (navigator as Navigator & {vendor?: string}).vendor || '';
    if (/windows phone/i.test(userAgent)) {
        return 'Windows Phone';
    }
    if (/android/i.test(userAgent)) {
        return 'Android';
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !(window as Window & {MSStream?: unknown}).MSStream) {
        return 'iOS';
    }
    if (/Windows NT|Macintosh|Linux|CrOS/.test(userAgent)) {
        return 'Web';
    }
    return 'Unknown';
}

export class WootricService {
    private scriptLoaded = false;
    private loadingPromise: Promise<void> | null = null;
    private consentUnsubscribe: (() => void) | null = null;
    private pendingRun: WootricRunOptions | null = null;
    private runStarted = false;
    private readonly config: WootricConfig = getWootricConfig();

    destroy(): void {
        this.consentUnsubscribe?.();
        this.consentUnsubscribe = null;
        this.pendingRun = null;
        this.runStarted = false;
        this.stop();
    }

    async run(options: WootricRunOptions): Promise<void> {
        if (this.runStarted) {
            return;
        }
        if (!this.config.enabled || !this.config.accountToken) {
            return;
        }
        if (!options.email?.trim()) {
            return;
        }
        if (isEmailExcluded(options.email, this.config.excludedDomains)) {
            return;
        }
        if (!hasAnalyticsCookieConsent()) {
            this.pendingRun = options;
            this.ensureConsentListener();
            return;
        }

        this.pendingRun = null;
        this.runStarted = true;
        const settings = this.buildSettings(options);
        this.applySettings(settings);
        await this.loadScript();
        this.invokeRun(1);
    }

    stop(): void {
        if (window.WootricSurvey?.stop) {
            window.WootricSurvey.stop();
        }
    }

    private ensureConsentListener(): void {
        if (this.consentUnsubscribe) {
            return;
        }
        this.consentUnsubscribe = subscribeToCookieConsentChanges((hasConsent) => {
            if (hasConsent && this.pendingRun) {
                void this.run(this.pendingRun);
            }
        });
    }

    private loadScript(): Promise<void> {
        if (this.scriptLoaded && window.wootric) {
            return Promise.resolve();
        }
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        this.loadingPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = this.config.cdnUrl;
            script.async = true;
            script.onload = () => {
                this.scriptLoaded = true;
                resolve();
            };
            script.onerror = () => {
                this.loadingPromise = null;
                reject(new Error('Failed to load Wootric SDK'));
            };
            document.head.appendChild(script);
        });

        return this.loadingPromise;
    }

    private buildSettings(options: WootricRunOptions): Record<string, unknown> {
        const settings: Record<string, unknown> = {
            email: options.email,
            account_token: this.config.accountToken,
            product_name: this.config.productName,
            modal_theme: options.modalTheme ?? 'light',
            created_at: options.createdAt,
            properties: {
                customerName: options.customerName,
                userRole: options.userRole,
                productReportingCode: this.config.productReportingCode,
                product: this.config.productName,
                deviceOS: getDeviceOS()
            },
            disclaimer: DISCLAIMER
        };

        return settings;
    }

    private applySettings(settings: Record<string, unknown>): void {
        if (this.config.testMode) {
            window.wootric_no_surveyed_cookie = true;
            window.wootric_survey_immediately = true;
        }

        window.wootricSettings = settings;
    }

    private invokeRun(attempt: number): void {
        if (window.wootric) {
            window.wootric('run');
            return;
        }
        if (attempt < MAX_RUN_ATTEMPTS) {
            setTimeout(() => this.invokeRun(attempt + 1), RUN_RETRY_MS);
        }
    }
}
